import { CognitoError, type CognitoTokens } from "./cognito";

export type EmailAccessMode = "SIGN_IN" | "SIGN_UP";
export type EmailAccessOutcome = "SIGN_IN_CODE" | "SIGN_UP_CODE" | "RESUMED_SIGN_UP_CODE";
export type EmailCodeConfirmationOutcome =
  | "AUTHENTICATED"
  | "ACCOUNT_CONFIRMATION_CODE"
  | "SIGN_IN_CODE";

export type EmailCodeConfirmationResult = {
  tokens: CognitoTokens | null;
  outcome: EmailCodeConfirmationOutcome;
};

type Actions = {
  signIn: () => Promise<CognitoTokens | null>;
  signUp: () => Promise<CognitoTokens | null>;
  resumeSignUp: () => Promise<CognitoTokens | null>;
};

export const ownerPreviewEnabled = import.meta.env.VITE_OWNER_PREVIEW_ENABLED === "true";

export function ownerPreviewSignInOnlyError() {
  return new CognitoError(
    "OWNER_PREVIEW_SIGN_IN_ONLY",
    "Use an existing account to sign in. If you need help getting access, contact support.",
  );
}

function hasCode(error: unknown, ...codes: string[]) {
  return error instanceof CognitoError && codes.includes(error.code);
}

export async function confirmSignInEmailCode(
  actions: {
    authenticate: () => Promise<CognitoTokens>;
    requestAccountConfirmation: () => Promise<void>;
  },
  options: { ownerPreview?: boolean } = {},
): Promise<EmailCodeConfirmationResult> {
  try {
    return { tokens: await actions.authenticate(), outcome: "AUTHENTICATED" };
  } catch (error) {
    // NotAuthorizedException also represents an expired/stale EMAIL_OTP challenge. Treating it
    // as an unconfirmed account switches the UI to the wrong code type and makes a valid sign-in
    // code impossible to use. Cognito reports the actual account state explicitly when recovery
    // is appropriate.
    if (!hasCode(error, "UserNotConfirmedException")) throw error;
    if (options.ownerPreview ?? ownerPreviewEnabled) throw ownerPreviewSignInOnlyError();
    await actions.requestAccountConfirmation();
    return { tokens: null, outcome: "ACCOUNT_CONFIRMATION_CODE" };
  }
}

async function signInOrResume(actions: Actions) {
  try {
    return { tokens: await actions.signIn(), outcome: "SIGN_IN_CODE" as const };
  } catch (error) {
    if (!hasCode(error, "UserNotConfirmedException")) throw error;
    return { tokens: await actions.resumeSignUp(), outcome: "RESUMED_SIGN_UP_CODE" as const };
  }
}

export async function beginEmailAccess(
  mode: EmailAccessMode,
  actions: Actions,
  options: { ownerPreview?: boolean } = {},
) {
  if (options.ownerPreview ?? ownerPreviewEnabled)
    return { tokens: await actions.signIn(), outcome: "SIGN_IN_CODE" as const };

  if (mode === "SIGN_UP") {
    try {
      return { tokens: await actions.signUp(), outcome: "SIGN_UP_CODE" as const };
    } catch (error) {
      if (!hasCode(error, "UsernameExistsException")) throw error;
      // With user-existence protection enabled, ResendConfirmationCode can return a decoy
      // success for a confirmed account. Attempt the real sign-in challenge first; Cognito gives
      // an explicit UserNotConfirmedException when confirmation recovery is genuinely needed.
      return signInOrResume(actions);
    }
  }

  try {
    return await signInOrResume(actions);
  } catch (error) {
    if (
      !hasCode(error, "EMAIL_OTP_NOT_AVAILABLE", "UserNotFoundException", "NotAuthorizedException")
    )
      throw error;
    try {
      return { tokens: await actions.signUp(), outcome: "SIGN_UP_CODE" as const };
    } catch (signUpError) {
      if (!hasCode(signUpError, "UsernameExistsException")) throw signUpError;
      return signInOrResume(actions);
    }
  }
}
